import {
  clampName,
  hostFromUrl,
  joinLines,
  parseCsvToObjects,
  passwordData,
  str,
  truthyFlag,
  withFolder,
} from './portabilityBasics';

/* ──────────────────────── Bitwarden field helpers ──────────────────────── */

/** Bitwarden `fields:[{name,value,type}]` → "name: value" text lines. */
export function bwFieldsToLines(fields) {
  if (!Array.isArray(fields)) return '';
  return fields
    .filter((f) => f && (str(f.name) !== '' || str(f.value) !== ''))
    .map((f) => {
      const name = str(f.name).trim();
      const value = str(f.value);
      return name ? `${name}: ${value}` : value;
    })
    .join('\n');
}

/** Bitwarden `fields[]` → custom { k, v, secret } entries (type 1 = hidden). */
export function appendBwFields(fields, out) {
  if (!Array.isArray(fields)) return;
  for (const f of fields) {
    if (!f || (str(f.name) === '' && str(f.value) === '')) continue;
    out.push({ k: str(f.name), v: str(f.value), secret: f.type === 1 || f.type === '1' });
  }
}

/**
 * Copy of a decrypted `data` blob without passkey material. Passkey records
 * carry private key material (`passkeys[].privateKey` / `prfSecret`), which must
 * never reach a plaintext export file, so the whole `passkeys` array and its
 * tombstone list are dropped — there is no passkey export interop and the
 * record metadata is worthless without the key. Shallow copy: the caller's
 * object is live in the store and must not be mutated.
 *
 * NOTE: an sftp item's top-level `data.privateKey` is the SSH key, is unrelated,
 * and is deliberately left alone.
 */
export function stripPasskeys(data) {
  const out = { ...(data || {}) };
  delete out.passkeys;
  delete out.passkeysDeleted;
  return out;
}

/** Push a { k, v, secret } custom field only when the value is non-empty. */
export function pushField(out, k, v, secret = false) {
  const value = str(v);
  if (value.trim() === '') return;
  out.push({ k, v: value, secret });
}

/* ─────────────────────────── Bitwarden JSON ─────────────────────────────── */

export function normalizeBitwardenEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const favorite = !!entry.favorite;
  const type = entry.type;

  // 1 = login → password
  if (type === 1) {
    const login = entry.login || {};
    const uri =
      Array.isArray(login.uris) && login.uris.length ? str(login.uris[0] && login.uris[0].uri) : '';
    const extra = bwFieldsToLines(entry.fields);
    const notes = joinLines([entry.notes, extra]);
    const fido = Array.isArray(login.fido2Credentials)
      ? login.fido2Credentials.filter((c) => c && typeof c === 'object')
      : [];
    // A login whose only content is a passkey (login.fido2Credentials) still
    // counts as content — dropping it would lose the name/URL the credential
    // belongs to.
    const hasContent =
      str(login.username) || str(login.password) || uri || str(entry.notes) || extra || fido.length;
    if (str(entry.name) === '' && !hasContent) return null;
    // Such an entry usually carries no URI either, and every one of them would
    // then normalize to the same nameless, hostless item and be deduped down to
    // one on import. The credential's own rpId is the site it belongs to, so
    // fall back to it for the URL — which gives the item a real name and a
    // distinct dedupe key. Metadata only; no key material is read.
    const rpId = str(fido[0] && fido[0].rpId).trim();
    const url = uri || (rpId ? `https://${rpId}` : '');
    const name = clampName(entry.name, hostFromUrl(url) || 'Login');
    return {
      type: 'password',
      name,
      favorite,
      data: passwordData({
        url,
        username: login.username,
        password: login.password,
        totp: login.totp,
        notes,
      }),
      // Carried OUTSIDE `data` on purpose. §2.6(b) makes `vault_put_item`
      // reject any payload that introduces passkey material, so these never go
      // through the item write — the caller saves the item, then hands this
      // array to the `passkey_import` command, which is one of the few writers
      // allowed to add credentials. Raw Bitwarden shape: the conversion to a
      // PasskeyRecord needs P-256 and a CSPRNG and belongs in Rust.
      ...(fido.length ? { passkeys: fido } : {}),
    };
  }

  // 2 = secure note → note
  if (type === 2) {
    const extra = bwFieldsToLines(entry.fields);
    const body = joinLines([entry.notes, extra]);
    if (str(entry.name) === '' && body === '') return null;
    return {
      type: 'note',
      name: clampName(entry.name, 'Secure note'),
      favorite,
      data: { body },
    };
  }

  // 3 = card → custom
  if (type === 3) {
    const card = entry.card || {};
    const fields = [];
    pushField(fields, 'Cardholder name', card.cardholderName);
    pushField(fields, 'Number', card.number, true);
    pushField(fields, 'Brand', card.brand);
    pushField(fields, 'Expiration month', card.expMonth);
    pushField(fields, 'Expiration year', card.expYear);
    pushField(fields, 'Security code', card.code, true);
    appendBwFields(entry.fields, fields);
    const notes = str(entry.notes).trim();
    if (fields.length === 0 && notes === '' && str(entry.name) === '') return null;
    const data = { fields };
    if (notes) data.notes = notes;
    return { type: 'custom', name: clampName(entry.name, 'Card'), favorite, data };
  }

  // 4 = identity → custom
  if (type === 4) {
    const id = entry.identity || {};
    const fields = [];
    pushField(fields, 'Title', id.title);
    pushField(fields, 'First name', id.firstName);
    pushField(fields, 'Middle name', id.middleName);
    pushField(fields, 'Last name', id.lastName);
    pushField(fields, 'Username', id.username);
    pushField(fields, 'Company', id.company);
    pushField(fields, 'Email', id.email);
    pushField(fields, 'Phone', id.phone);
    pushField(fields, 'Address 1', id.address1);
    pushField(fields, 'Address 2', id.address2);
    pushField(fields, 'Address 3', id.address3);
    pushField(fields, 'City', id.city);
    pushField(fields, 'State', id.state);
    pushField(fields, 'Postal code', id.postalCode);
    pushField(fields, 'Country', id.country);
    pushField(fields, 'SSN', id.ssn, true);
    pushField(fields, 'Passport number', id.passportNumber, true);
    pushField(fields, 'License number', id.licenseNumber, true);
    appendBwFields(entry.fields, fields);
    const notes = str(entry.notes).trim();
    if (fields.length === 0 && notes === '' && str(entry.name) === '') return null;
    const nameFromParts = joinLines([id.firstName, id.lastName]).replace(/\n/g, ' ').trim();
    const data = { fields };
    if (notes) data.notes = notes;
    return {
      type: 'custom',
      name: clampName(entry.name, nameFromParts || 'Identity'),
      favorite,
      data,
    };
  }

  return null; // unknown/unsupported Bitwarden item type
}

/**
 * How many passkeys a Bitwarden entry carries. Surfaced on the parse result so
 * the preview screen can say how many credentials are about to come across —
 * the import is otherwise silent about them and the user has no way to tell a
 * successful passkey import from a dropped one.
 *
 * Counted for entries that fail to normalize too: their passkeys are lost with
 * them, and the number the preview shows must be the honest total in the file.
 */
export function countBitwardenPasskeys(entry) {
  const fido = entry && entry.login && entry.login.fido2Credentials;
  return Array.isArray(fido) ? fido.filter((c) => c && typeof c === 'object').length : 0;
}

export function parseBitwardenJson(text) {
  const items = [];
  let skipped = 0;
  let passkeysFound = 0;
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { items, skipped: 0, passkeysFound: 0 };
  }
  const list = Array.isArray(parsed && parsed.items)
    ? parsed.items
    : Array.isArray(parsed)
      ? parsed
      : [];
  // Bitwarden JSON keeps folders NORMALISED — a top-level `folders: [{id,name}]`
  // and a `folderId` on each item. Resolve to the NAME here: our vault has no
  // folder ids, and an unresolvable folderId (foreign export, pruned folder)
  // must degrade to "unfiled", never to a folder literally named the uuid.
  const folderNames = new Map();
  if (Array.isArray(parsed && parsed.folders)) {
    for (const f of parsed.folders) {
      if (f && typeof f === 'object' && str(f.id) && str(f.name).trim()) {
        folderNames.set(str(f.id), str(f.name).trim());
      }
    }
  }
  for (const entry of list) {
    try {
      passkeysFound += countBitwardenPasskeys(entry);
    } catch {
      /* a hostile entry must not abort the import — the count is advisory */
    }
    let norm = null;
    try {
      norm = normalizeBitwardenEntry(entry);
    } catch {
      norm = null;
    }
    if (norm) {
      const fid = entry && typeof entry === 'object' ? str(entry.folderId) : '';
      items.push(withFolder(norm, fid ? folderNames.get(fid) : ''));
    } else {
      skipped += 1;
    }
  }
  return { items, skipped, passkeysFound };
}

/* ──────────────────────────── Bitwarden CSV ─────────────────────────────── */
// columns: folder,favorite,type,name,notes,fields,reprompt,login_uri,
//          login_username,login_password,login_totp

export function parseBitwardenCsv(text) {
  const { records } = parseCsvToObjects(text);
  const items = [];
  let skipped = 0;
  for (const r of records) {
    const type = str(r.type).trim().toLowerCase();
    const favorite = truthyFlag(r.favorite);
    if (type === 'login') {
      const url = str(r.login_uri);
      const notes = joinLines([r.notes, r.fields]);
      // Skip content-less login rows (no username/password/uri/notes) instead of
      // creating a junk empty "Login" item; count them like other bad rows.
      const hasContent =
        str(r.login_username).trim() || str(r.login_password) || url.trim() || notes;
      if (str(r.name).trim() === '' && !hasContent) {
        skipped += 1;
        continue;
      }
      const name = clampName(r.name, hostFromUrl(url) || 'Login');
      items.push(
        withFolder(
          {
            type: 'password',
            name,
            favorite,
            data: passwordData({
              url,
              username: r.login_username,
              password: r.login_password,
              totp: r.login_totp,
              notes,
            }),
          },
          r.folder
        )
      );
    } else if (type === 'note' || type === 'securenote') {
      const body = joinLines([r.notes, r.fields]);
      if (str(r.name) === '' && body === '') {
        skipped += 1;
        continue;
      }
      items.push(
        withFolder(
          { type: 'note', name: clampName(r.name, 'Secure note'), favorite, data: { body } },
          r.folder
        )
      );
    } else {
      skipped += 1;
    }
  }
  return { items, skipped };
}
